import React, { useEffect, useRef, useState } from 'react';
import { ChevronUp } from 'lucide-react';
import { useTranslate } from '../../lib/i18n';
import MarkdownRenderer from '../MarkdownRenderer';
import ResearchConsentButtons from './ResearchConsentButtons';

interface ResearchConsentCardProps {
  content: string;
  isExpanded: boolean;
  onToggle: () => void;
  onResponse: (consentGiven: boolean) => void;
  hasResponded: boolean;
  currentConsent?: boolean | null;
}

/**
 * Component for displaying research consent as an expandable card similar to information cards
 */
const ResearchConsentCard = ({
  content,
  isExpanded,
  onToggle,
  onResponse,
  hasResponded,
  currentConsent,
}: ResearchConsentCardProps) => {
  const t = useTranslate();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxContentHeight, setMaxContentHeight] = useState<number>(200); // Start with reasonable initial height

  const title = hasResponded
    ? t('researchConsent.thanksTitle', { defaultValue: 'Research Consent' })
    : t('researchConsent.title', { defaultValue: 'Research Consent' });

  // Calculate precise height based on actual content + buttons
  useEffect(() => {
    if (contentRef.current && isExpanded) {
      // Wait for next frame to ensure content is rendered
      requestAnimationFrame(() => {
        if (contentRef.current) {
          // Measure the actual scrollHeight of the rendered content
          const actualHeight = contentRef.current.scrollHeight;
          // Add minimal padding for breathing room (just 10px)
          setMaxContentHeight(actualHeight + 10);
        }
      });
    }
  }, [content, isExpanded, hasResponded, currentConsent]);

  return (
    <div className="w-full overflow-hidden rounded border bg-card text-card-foreground min-w-0">
      <div
        className="relative flex cursor-pointer items-center bg-muted px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onToggle();
          }
        }}
      >
        <div className="flex min-w-0 flex-1 justify-center">
          <span className="pr-4 text-center font-button">{title}</span>
        </div>
        <div className="absolute right-2">
          <ChevronUp
            className={`transform transition-transform ${isExpanded ? '' : 'rotate-180'}`}
            style={{ transitionDuration: '500ms' }}
          />
        </div>
      </div>
      <div
        ref={contentRef}
        className="relative transition-all duration-500 ease-in-out"
        style={{
          height: isExpanded ? `${maxContentHeight}px` : '0px',
          opacity: isExpanded ? 1 : 0,
          overflow: isExpanded ? 'visible' : 'hidden',
        }}
      >
        <div className="relative border-t">
          <div className="px-2 pt-4 pb-4 sm:px-4">
            <div className="whitespace-normal break-words text-sm">
              <MarkdownRenderer markdown={content} />
            </div>

            <ResearchConsentButtons
              onResponse={onResponse}
              hasResponded={hasResponded}
              currentConsent={currentConsent}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchConsentCard;
